import Vue from 'vue';
import type { Filters } from '@mimir/common/filters/Filters';
import { ChoiceFilter, createCustomFilterBasedOnField, createFilterKey } from '@mimir/common/filters/Filters';
import { getFiltersFromSearchString } from '@mimir/common/filters/filter-util';
import type { FilterToken, PositionedFilterToken, SearchOperator, SearchToken, ValueGap } from '@mimir/common/filters/searchTokens';
import type { OperatorCapability } from '@mimir/common/filters/operatorSupport';
import { OPERATOR_CAPABILITY } from '@mimir/common/filters/operatorSupport';
import { consumedFilterRanges, positionedFilterTokens, removeRanges, tokenizeSearchString, valueOperators } from '@mimir/common/filters/searchTokens';
import type { MetaDataSchema } from '@mimir/common/mdf/metadataSchema';
import type { MdfFieldEntity } from '@mimir/common/mdf/fieldDefinition';
import { DEFAULT_MDF_ID } from '@mimir/common/mdf/mdfDefaults';
import { StaticFileKeywords, StaticFilterKeywords } from '@mimir/common/dto/searchModels';
import type { UserInfoDto } from '@mimir/common/dto/userDto';
import type { OptionListDto } from '@mimir/common/dto/optionListDto';
import type { DateHelper } from '@mimir/common/util/DateUtil';
import type { Logger } from '@mimir/common/types/logger';

/** The inputs the search box hands in to turn its raw text into filter chips: the typed
 *  searchString, the metadata schemas (mdfs) to match field names against, the reactive Filters
 *  state to fill, and the lookups a field needs to parse its value (users, option lists, dates). */
export interface AddCustomFiltersContext {
  searchString: string;
  mdfs: MetaDataSchema[];
  filters: Filters;
  users: UserInfoDto[];
  optionLists: OptionListDto[];
  dateHelper: DateHelper;
  logger: Logger;
}

/** Built-in filter keys (formId, type, the file keywords) that the search box handles on its
 *  own, so the metadata-field pass below skips them. */
const STATIC_FILTER_KEYS: ReadonlySet<string> = new Set<string>([
  ...Object.values(StaticFilterKeywords),
  ...Object.values(StaticFileKeywords),
]);

/** The `formId:` that prefixes the term naming the form, e.g. `formId:news-story`. */
const FORM_ID_PREFIX = `${StaticFilterKeywords.FormId}:`;

/**
 * Reads the metadata-field filters out of `searchString`, adds each one as a chip on
 * `context.filters` (mutated in place, since that is the reactive state the search box owns),
 * and returns the leftover free text with the consumed terms removed. So 'sunset person:obama'
 * adds a person chip and returns 'sunset'. The caller commits the returned string.
 */
export function addCustomFiltersToSearchString(context: AddCustomFiltersContext): string {
  const { searchString, mdfs, filters } = context;
  if (mdfs.length === 0) {
    return searchString;
  }

  const terms = getFiltersFromSearchString(searchString) ?? [];
  const formIdTerm = terms.find(term => term.startsWith(FORM_ID_PREFIX));
  const namedFormId = formIdTerm?.slice(FORM_ID_PREFIX.length);

  const mdf = findMdf(namedFormId || DEFAULT_MDF_ID, mdfs);
  if (!mdf) {
    return searchString;
  }
  storeFormIdFilter(mdf, filters);

  const withoutFormId = formIdTerm ? searchString.replace(formIdTerm, '') : searchString;
  return applyFieldFilters(terms, mdf, withoutFormId, context);
}

/** Finds the form by id or label, e.g. 'interview' or its label. */
function findMdf(formIdOrLabel: string, mdfs: MetaDataSchema[]): MetaDataSchema | undefined {
  return mdfs.find(schema => schema.id === formIdOrLabel || schema.label === formIdOrLabel);
}

/** Reflects the chosen form on the formId chip. Kept apart from findMdf because validateOperators
 *  resolves a form too, and a check that only reads the search must not change it. The default
 *  form is implicit and gets no chip. */
function storeFormIdFilter(mdf: MetaDataSchema, filters: Filters): void {
  if (mdf.id === DEFAULT_MDF_ID) {
    return;
  }

  const formIdFilter = filters.defaultFilters['filter:formId'];
  formIdFilter.alias = mdf.label;
  formIdFilter.parseValue(mdf.label, formIdFilter.hasSticky);
}

/** Turns each `field:value` term into a custom filter chip and strips the consumed terms from
 *  the text. Skips built-in keys, unknown fields, a value already on that chip, and values that
 *  parse to nothing. So 'person:obama' becomes a person chip and leaves the rest as free text. */
function applyFieldFilters(terms: string[], mdf: MetaDataSchema, searchString: string, context: AddCustomFiltersContext): string {
  const fieldsById = buildFieldLookup(mdf);
  let residual = searchString;

  for (const term of terms) {
    const [fieldId, value] = term.split(':');
    if (STATIC_FILTER_KEYS.has(fieldId)) {
      continue;
    }

    const field = fieldsById.get(fieldId);
    if (!field) {
      continue;
    }

    if (addCustomFilterForField(field, value, mdf, context)) {
      residual = residual.replace(term, '');
    }
  }
  return residual;
}

/** Creates the custom filter for `field` with `value` and stores it on `context.filters`,
 *  unless an equal value is already there or the value is empty. Returns whether it stored one,
 *  which is the signal to drop the matching term from the leftover text. */
function addCustomFilterForField(field: MdfFieldEntity, value: string, mdf: MetaDataSchema, context: AddCustomFiltersContext): boolean {
  const key = createFilterKey(field);
  const existing = context.filters.customFilters[key];
  if (existing && existing.getValue() === value) {
    return false;
  }

  const filter = createCustomFilterBasedOnField(context.logger, field, mdf.id, context.dateHelper, undefined, context.users, context.optionLists);
  filter.parseValue(value);
  if (!filter.hasValue) {
    return false;
  }

  Vue.set(context.filters.customFilters, key, filter);
  return true;
}

/** Indexes a form's fields by every id a term might use: the fieldId a user types, the entity id,
 *  and the chip key, which is what a search rebuilt from the URL carries. */
function buildFieldLookup(mdf: MetaDataSchema): Map<string, MdfFieldEntity> {
  const fieldsById = new Map<string, MdfFieldEntity>();
  for (const field of mdf.fields ?? []) {
    fieldsById.set(field.fieldId, field);
    fieldsById.set(field.id, field);
    fieldsById.set(createFilterKey(field), field);
  }
  return fieldsById;
}

/** A field together with every token that targeted it in one parse. The tokens keep the index they
 *  sat at, which is the only way to read the operator the user typed between two of them. */
interface FieldTokenGroup {
  field: MdfFieldEntity;
  key: string;
  tokens: PositionedFilterToken[];
}

/** Null when there is nothing to resolve: no schemas, or no form matched. */
interface ResolvedFieldGroups {
  tokens: SearchToken[];
  formIdToken: FilterToken | undefined;
  mdf: MetaDataSchema;
  groups: FieldTokenGroup[];
}

/** The shared front half of every token-based pass over the search box: tokenize, pick the form,
 *  and bucket the field tokens by field. applyCustomFilters, applyMultiValueOperators, and
 *  validateOperators all start here so the resolution lives in one place. */
function resolveFieldGroups(searchString: string, mdfs: MetaDataSchema[]): ResolvedFieldGroups | null {
  if (mdfs.length === 0) {
    return null;
  }

  const tokens = tokenizeSearchString(searchString);
  const filterTokens = positionedFilterTokens(tokens);
  const formIdToken = filterTokens.find(({ token }) => token.field === StaticFilterKeywords.FormId)?.token;

  const mdf = findMdf(formIdToken?.value || DEFAULT_MDF_ID, mdfs);
  if (!mdf) {
    return null;
  }

  return { tokens, formIdToken, mdf, groups: groupTokensByField(filterTokens, buildFieldLookup(mdf)) };
}

/** The new-parser twin of addCustomFiltersToSearchString. A field typed more than once keeps every
 *  value. Consumed filter terms, and any operator left stranded next to one, are cut from the
 *  returned free text. */
export function applyCustomFilters(context: AddCustomFiltersContext): string {
  const resolved = resolveFieldGroups(context.searchString, context.mdfs);
  if (!resolved) {
    return context.searchString;
  }
  const { tokens, formIdToken, mdf, groups } = resolved;
  storeFormIdFilter(mdf, context.filters);

  const consumed = new Set<SearchToken>();
  if (formIdToken) {
    consumed.add(formIdToken);
  }

  for (const group of groups) {
    if (storeFieldFilter(group, mdf, context, tokens)) {
      for (const { token } of group.tokens) {
        consumed.add(token);
      }
    }
  }

  return removeRanges(context.searchString, consumedFilterRanges(tokens, consumed));
}


/** Buckets the tokens by resolved field, so repeats of one field share a group and their values
 *  stack. Static keys and unknown fields are dropped. */
function groupTokensByField(filterTokens: PositionedFilterToken[], fieldsById: Map<string, MdfFieldEntity>): FieldTokenGroup[] {
  const groupsByKey = new Map<string, FieldTokenGroup>();
  for (const positioned of filterTokens) {
    if (STATIC_FILTER_KEYS.has(positioned.token.field)) {
      continue;
    }

    const field = fieldsById.get(positioned.token.field);
    if (!field) {
      continue;
    }

    const key = createFilterKey(field);
    const group = groupsByKey.get(key);
    if (group) {
      group.tokens.push(positioned);
    } else {
      groupsByKey.set(key, { field, key, tokens: [positioned] });
    }
  }
  return [...groupsByKey.values()];
}

/** A fresh filter per call, so a new search resets the field instead of stacking onto the last one.
 *  Whether repeated values stack or the last wins is the filter type's own behaviour: text and
 *  choice stack, user and checkbox keep the last. */
function storeFieldFilter(group: FieldTokenGroup, mdf: MetaDataSchema, context: AddCustomFiltersContext, tokens: SearchToken[]): boolean {
  const filter = createCustomFilterBasedOnField(context.logger, group.field, mdf.id, context.dateHelper, undefined, context.users, context.optionLists);
  for (const positioned of group.tokens) {
    filter.parseValue(positioned.token.value);
  }
  if (!filter.hasValue) {
    return false;
  }

  if (filter instanceof ChoiceFilter) {
    filter.multiValueOperators = groupValueOperators(tokens, group);
  }
  Vue.set(context.filters.customFilters, group.key, filter);
  return true;
}

/** The operator the user typed in each gap between the group's values, one entry per gap. */
function groupValueOperators(tokens: SearchToken[], group: FieldTokenGroup): ValueGap[] {
  return valueOperators(tokens, group.tokens.map(({ index }) => index));
}

/** URL half of the operator round-trip, for the new-parsing path: reads the operator typed
 *  between repeated values of one choice field in the restored search string and sets it on the
 *  already rebuilt chip, so a reloaded or shared search keeps its OR/AND. */
export function applyMultiValueOperators(searchString: string, mdfs: MetaDataSchema[], filters: Filters): void {
  const resolved = resolveFieldGroups(searchString, mdfs);
  if (!resolved) {
    return;
  }

  for (const group of resolved.groups) {
    const filter = filters.customFilters[group.key];
    if (filter instanceof ChoiceFilter) {
      filter.multiValueOperators = groupValueOperators(resolved.tokens, group);
    }
  }
}

/** Something wrong with the operators a search carries. `field` is the offending field's id. */
export interface SearchOperatorIssue {
  kind: 'and-on-single-value' | 'mixed-operators' | 'operator-not-supported';
  field: string;
}

/** Static filters whose repeated values already match their union, so a typed OR gets what it asks
 *  for even though the operator itself is not read. Warning on those would be false. */
const ALWAYS_UNION_KEYS: ReadonlySet<string> = new Set<string>([
  StaticFilterKeywords.ItemType,
  StaticFilterKeywords.ItemState,
]);

/** AND across a single-choice field's values can never match, since an item holds one value there.
 *  Operators across different fields are never a conflict. */
export function validateOperators(searchString: string, mdfs: MetaDataSchema[]): SearchOperatorIssue | null {
  // Built-in keywords are matched by key, so they are checked even when no form resolves.
  const resolved = resolveFieldGroups(searchString, mdfs);
  const tokens = resolved?.tokens ?? tokenizeSearchString(searchString);

  for (const group of resolved?.groups ?? []) {
    // Only the gaps the user actually typed an operator in can conflict; a blank gap resolves to
    // the field's own default rather than to a second operator.
    const typed = new Set(groupValueOperators(tokens, group).filter(operator => operator !== null));
    const issue = operatorIssueFor(group.field.fieldId, OPERATOR_CAPABILITY[group.field.type], typed);
    if (issue) {
      return issue;
    }
  }
  return staticOperatorIssue(tokens);
}

/** The first thing wrong with the operators typed on one field, worst first: a combination no item
 *  can match, then an operator the field ignores, then a mix that has no single meaning. */
function operatorIssueFor(field: string, capability: OperatorCapability, typed: ReadonlySet<SearchOperator>): SearchOperatorIssue | null {
  if (typed.size === 0) {
    return null;
  }

  if (!capability.multiValued && typed.has('AND')) {
    return { kind: 'and-on-single-value', field };
  }
  if (!capability.honoursOperator) {
    return { kind: 'operator-not-supported', field };
  }
  if (typed.size > 1) {
    return { kind: 'mixed-operators', field };
  }
  return null;
}

/** Static filters are matched by key, and the value merge that reads the operator only runs for
 *  metadata fields, so an operator between two static values is dropped and the values ANDed. */
function staticOperatorIssue(tokens: SearchToken[]): SearchOperatorIssue | null {
  const byKey = new Map<string, number[]>();
  for (const { index, token } of positionedFilterTokens(tokens)) {
    if (!STATIC_FILTER_KEYS.has(token.field) || ALWAYS_UNION_KEYS.has(token.field)) {
      continue;
    }

    const indices = byKey.get(token.field);
    if (indices) {
      indices.push(index);
    } else {
      byKey.set(token.field, [index]);
    }
  }

  for (const [field, indices] of byKey) {
    if (valueOperators(tokens, indices).some(operator => operator !== null)) {
      return { kind: 'operator-not-supported', field };
    }
  }
  return null;
}

export function describeOperatorIssue(issue: SearchOperatorIssue): string {
  switch (issue.kind) {
    case 'and-on-single-value':
      return `"${issue.field}" holds one value, so AND between its values never matches. Use OR.`;
    case 'mixed-operators':
      return `Mixing AND and OR on "${issue.field}" is ambiguous. Use one operator for that field.`;
    case 'operator-not-supported':
      return `Only choice and multi-select fields support AND or OR between values, so "${issue.field}" combines its values with AND.`;
  }
}
